import type { FileMap, File } from '.';
import { addWorkDir } from '~/utils/workDir';

type FileMapSearchResult = readonly [filePath: string | undefined, file: File | undefined];
type FileMapSearch = (files: FileMap) => FileMapSearchResult;

const DEFAULT_SELECTED_FILE_LIST: (string | FileMapSearch)[] = [
  // most of our starters & projects
  'README.md',

  // `bolt-expo`
  'app/_layout.tsx',

  // `nextjs-shadcn`
  'app/page.tsx',

  // searching for a file with 'Root', 'App' or 'main' in the name
  (files: FileMap) => {
    let result: File | undefined;
    let resultFilePath: string | undefined;

    for (const [filePath, file] of Object.entries(files)) {
      // if we've found a match that is smaller we skip this one
      if ((resultFilePath && resultFilePath.length < filePath.length) || file?.type !== 'file') {
        continue;
      }

      const filename = filePath.slice(filePath.lastIndexOf('/') + 1);
      const [beforeExtension, extension] = filename.split('.');

      // if the name is not valid
      if (!beforeExtension.match(/^(main|app|root)$/i)) {
        continue;
      }

      // if the extension is not valid
      if (!extension.match(/^(?:tsx?|jsx?)$/)) {
        continue;
      }

      result = file;
      resultFilePath = filePath;
    }

    return [resultFilePath, result] as const;
  },

  // last good default
  'package.json',

  // fallback to any file present if any
  (files: FileMap) => {
    const result = Object.entries(files).find(([_, file]) => file && file?.type === 'file') as
      | FileMapSearchResult
      | undefined;

    return result ?? ([undefined, undefined] as const);
  },
].map((maybePath) => (typeof maybePath === 'string' ? addWorkDir(maybePath) : maybePath));

export function findDefaultSelectedFile(files: FileMap) {
  // try to find a good default file to show as selected
  for (const defaultSelectedFile of DEFAULT_SELECTED_FILE_LIST) {
    let file;
    let filePath;

    if (typeof defaultSelectedFile === 'string') {
      file = files[defaultSelectedFile];
      filePath = defaultSelectedFile;
    } else {
      [filePath, file] = defaultSelectedFile(files);
    }

    if (file?.type === 'file') {
      return filePath;
    }
  }

  return undefined;
}
